import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  ArrowRight, Banknote, Building2, Cpu, FileDown, FileSearch, Globe2,
  LayoutDashboard, Lock, Network, Palette, ShieldCheck, TerminalSquare, Users,
} from "lucide-react";
import { LogoMark } from "@/components/logo/AetriaLogo";
import LanguageSelector from "@/components/LanguageSelector";
import { PronounceButton } from "@/components/PronounceButton";
import { FALLBACK_BANDS, matchBand } from "@/lib/brandingOptions";
import { BandSlider, BandReadout, maxSubscribableCap } from "@/components/billing/BandSlider";

const API = process.env.REACT_APP_BACKEND_URL;
const BROCHURE_URL = `${API}/api/marketing/brochure.pdf`;

const GOLD = "var(--brand-accent)";
const GOLD_BRIGHT = "var(--brand-accent-bright)";
const GOLD_INK = "var(--brand-accent-ink)";

const NAV_LINKS = [
  ["landing.navPlatform", "platform", "#platform"], ["landing.navFeatures", "features", "#features"],
  ["landing.navSecurity", "security", "#security"], ["landing.navPricing", "pricing", "#pricing"],
];

const FEATURES = [
  { icon: Cpu, tid: "skills-intelligence-learning", titleKey: "landing.feature1Title", textKey: "landing.feature1Text" },
  { icon: Network, tid: "org-intelligence-what-if", titleKey: "landing.feature2Title", textKey: "landing.feature2Text" },
  { icon: FileSearch, tid: "ocr-document-compliance", titleKey: "landing.feature3Title", textKey: "landing.feature3Text" },
  { icon: Banknote, tid: "payroll-ai-performance", titleKey: "landing.feature4Title", textKey: "landing.feature4Text" },
  { icon: TerminalSquare, tid: "open-query-executive-ai", titleKey: "landing.feature5Title", textKey: "landing.feature5Text" },
  { icon: ShieldCheck, tid: "pii-masked-ai-gateway", titleKey: "landing.feature6Title", textKey: "landing.feature6Text" },
];

const ADVANTAGES = [
  { icon: Palette, titleKey: "landing.adv1Title", textKey: "landing.adv1Text" },
  { icon: Globe2, titleKey: "landing.adv2Title", textKey: "landing.adv2Text" },
  { icon: Lock, titleKey: "landing.adv3Title", textKey: "landing.adv3Text" },
  { icon: Building2, titleKey: "landing.adv4TitleRequest", textKey: "landing.adv4TextApproved" },
];

const PLAN_BULLETS = ["landing.planBullet1", "landing.planBullet2", "landing.planBullet3"];

const HeroMock = () => (
  <div className="border border-slate-700 bg-[#0F172A] shadow-2xl" data-testid="landing-hero-mock" aria-hidden="true">
    <div className="flex">
      <div className="w-36 shrink-0 border-r border-slate-700/70 p-3 hidden sm:block">
        <div className="flex items-center gap-1.5 mb-4">
          <LogoMark size={16} inverted />
          <span className="text-[8px] font-bold text-white tracking-wider">AETRIA <span style={{ color: GOLD_BRIGHT }}>OS</span></span>
        </div>
        {["Dashboard", "Employees", "Org Intelligence", "Payroll", "Documents", "Open Query"].map((l, i) => (
          <div key={l} className="text-[8px] px-2 py-1.5 mb-0.5"
            style={{
              color: i === 0 ? "#fff" : "#64748b",
              borderLeft: i === 0 ? `2px solid ${GOLD}` : "2px solid transparent",
              background: i === 0 ? "rgba(255,255,255,.05)" : "transparent",
            }}>{l}</div>
        ))}
      </div>
      <div className="flex-1 p-4 bg-[#F8FAFC]">
        <p className="text-[10px] font-bold text-[#1E293B]">Workforce Dashboard</p>
        <div className="grid grid-cols-3 gap-2 mt-2">
          {[["Headcount", "248"], ["Compliance", "96%"], ["Readiness", "8.2"]].map(([k, v]) => (
            <div key={k} className="bg-white border border-slate-200 p-2">
              <p className="text-[6px] uppercase tracking-wider text-slate-400">{k}</p>
              <p className="text-[13px] font-bold font-mono text-[#1E293B]">{v}</p>
            </div>
          ))}
        </div>
        <div className="bg-white border border-slate-200 p-2 mt-2">
          <p className="text-[6px] uppercase tracking-wider text-slate-400 mb-1.5">Skills coverage by department</p>
          <div className="flex items-end gap-1.5 h-12">
            {[62, 84, 45, 91, 70, 55, 78, 88].map((h, i) => (
              <div key={i} className="flex-1" style={{ height: `${h}%`, background: i === 3 ? GOLD : "#CBD5E1" }} />
            ))}
          </div>
        </div>
        <div className="bg-white border border-slate-200 mt-2">
          {[["Visa renewals due", "7", true], ["Reviews in progress", "12", false], ["Open positions", "4", false]].map(([k, v, hot]) => (
            <div key={k} className="flex justify-between px-2 py-1.5 border-b border-slate-100 last:border-0">
              <span className="text-[7px] text-slate-500">{k}</span>
              <span className="text-[7px] font-mono font-bold" style={{ color: hot ? GOLD_INK : "#1E293B" }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default function LandingPage() {
  const { t } = useTranslation();
  const [bands, setBands] = useState(FALLBACK_BANDS);
  const [count, setCount] = useState(35);
  useEffect(() => {
    axios.get(`${API}/api/signup/pricing`)
      .then((r) => { if (r.data.bands?.length) setBands(r.data.bands); })
      .catch(() => {});
  }, []);
  const band = matchBand(bands, count);
  const maxCap = maxSubscribableCap(bands);
  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="landing-page">
      {/* sticky nav */}
      <nav className="sticky top-0 z-40 bg-[#1E293B]/95 backdrop-blur border-b border-slate-700/60"
        data-testid="landing-nav">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-6">
          <div className="flex items-center gap-2 shrink-0">
            <a href="#top" className="flex items-center gap-2.5">
              <LogoMark size={30} inverted />
              <span className="text-white font-bold text-[16px] tracking-tight">
                AETRIA<span className="ml-1" style={{ color: GOLD_BRIGHT }}>OS</span>
              </span>
            </a>
            <PronounceButton dark testid="landing-pronounce-btn" />
          </div>
          <div className="hidden md:flex items-center gap-7">
            {NAV_LINKS.map(([key, tid, href]) => (
              <a key={tid} href={href} data-testid={`landing-nav-${tid}`}
                className="text-[12px] font-medium text-slate-300 hover:text-white transition-colors">
                {t(key)}
              </a>
            ))}
            <a href={BROCHURE_URL} target="_blank" rel="noreferrer" data-testid="landing-nav-brochure"
              className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-300 hover:text-white transition-colors">
              <FileDown className="h-3.5 w-3.5" /> {t("landing.navBrochure")}
            </a>
            <LanguageSelector dark />
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Link to="/login" data-testid="landing-login-btn"
              className="text-[12px] font-semibold uppercase tracking-[0.1em] text-white border border-slate-500 hover:border-white px-4 py-2 transition-colors">
              {t("landing.login")}
            </Link>
            <Link to="/signup" data-testid="landing-header-cta"
              className="text-[12px] font-semibold uppercase tracking-[0.1em] brand-accent-bg px-4 py-2 hidden sm:block">
              {t("landing.requestTrial")}
            </Link>
          </div>
        </div>
      </nav>

      {/* hero */}
      <header id="top" className="bg-[#1E293B]" data-testid="landing-hero">
        <div className="max-w-6xl mx-auto px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em]" style={{ color: GOLD_BRIGHT }}>
              {t("landing.heroEyebrow")}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mt-4 leading-[1.05]">
              {t("landing.heroTitle")}
            </h1>
            <p className="text-base md:text-lg text-slate-300 mt-6 max-w-xl leading-relaxed">
              {t("landing.heroSubtitle")}
            </p>
            <div className="mt-10 flex items-center gap-4 flex-wrap">
              <Link to="/signup" data-testid="landing-cta"
                className="inline-flex items-center gap-2 brand-accent-bg font-semibold px-7 py-3.5 text-[14px]">
                {t("landing.heroCtaRequest")} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </Link>
              <Link to="/login" data-testid="landing-hero-login"
                className="inline-flex items-center gap-2 text-white border border-slate-500 hover:border-white px-7 py-3.5 text-[14px] font-semibold transition-colors">
                {t("landing.login")}
              </Link>
            </div>
            <p className="text-[12px] text-slate-400 mt-4">{t("landing.reviewTagline")}</p>
          </div>
          <HeroMock />
        </div>
      </header>

      {/* platform strip */}
      <section id="platform" className="border-b border-slate-200 bg-white" data-testid="landing-platform">
        <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[[LayoutDashboard, t("landing.strip1Title"), t("landing.strip1Sub")],
            [Users, t("landing.strip2Title"), t("landing.strip2Sub")],
            [ShieldCheck, t("landing.strip3Title"), t("landing.strip3Sub")],
            [Palette, t("landing.strip4Title"), t("landing.strip4Sub")]].map(([Icon, title, s]) => (
            <div key={title} className="flex gap-3">
              <Icon className="h-5 w-5 shrink-0 mt-0.5" style={{ color: GOLD_INK }} />
              <div>
                <p className="text-[13px] font-bold text-[#1E293B]">{title}</p>
                <p className="text-[11.5px] text-slate-500 mt-0.5">{s}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* features */}
      <section id="features" className="max-w-6xl mx-auto px-6 py-20" data-testid="landing-features">
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em]" style={{ color: GOLD_INK }}>{t("landing.featuresEyebrow")}</p>
        <h2 className="text-base md:text-lg font-bold uppercase tracking-[0.16em] text-[#1E293B] mt-2">
          {t("landing.featuresTitle")}
        </h2>
        <div className="grid md:grid-cols-3 gap-4 mt-10">
          {FEATURES.map(({ icon: Icon, tid, titleKey, textKey }) => (
            <div key={tid} className="bg-white border border-slate-200 p-7 hover:border-slate-400 transition-colors"
              data-testid={`landing-feature-${tid}`}>
              <Icon className="h-5 w-5" style={{ color: GOLD_INK }} />
              <h3 className="text-[14px] font-bold text-[#1E293B] mt-4">{t(titleKey)}</h3>
              <p className="text-[12.5px] text-slate-500 mt-2 leading-relaxed">{t(textKey)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* security band */}
      <section id="security" className="bg-[#1E293B]" data-testid="landing-security">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em]" style={{ color: GOLD_BRIGHT }}>{t("landing.securityEyebrow")}</p>
          <h2 className="text-base md:text-lg font-bold uppercase tracking-[0.16em] text-white mt-2">
            {t("landing.securityTitle")}
          </h2>
          <div className="grid md:grid-cols-3 gap-8 mt-10">
            {[[t("landing.security1Title"), t("landing.security1Text")],
              [t("landing.security2Title"), t("landing.security2Text")],
              [t("landing.security3Title"), t("landing.security3Text")]].map(([title, s]) => (
              <div key={title}>
                <div className="h-px w-10 mb-4" style={{ background: GOLD }} />
                <p className="text-[14px] font-bold text-white">{title}</p>
                <p className="text-[12.5px] text-slate-400 mt-2 leading-relaxed">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* advantages strip */}
      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="landing-advantages">
        <div className="grid md:grid-cols-4 gap-4">
          {ADVANTAGES.map(({ icon: Icon, titleKey, textKey }) => (
            <div key={titleKey} className="bg-white border border-slate-200 p-6">
              <Icon className="h-5 w-5" style={{ color: GOLD_INK }} />
              <p className="text-[13px] font-bold text-[#1E293B] mt-3">{t(titleKey)}</p>
              <p className="text-[11.5px] text-slate-500 mt-1.5 leading-relaxed">{t(textKey)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* pricing — headcount band slider */}
      <section id="pricing" className="max-w-6xl mx-auto px-6 pb-20" data-testid="landing-pricing">
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em]" style={{ color: GOLD_INK }}>{t("landing.pricingEyebrow")}</p>
        <h2 className="text-base md:text-lg font-bold uppercase tracking-[0.16em] text-[#1E293B] mt-2">
          {t("landing.pricingTitleBands")}
        </h2>
        <div className="grid lg:grid-cols-5 gap-4 mt-10 items-start">
          <div className="lg:col-span-3 bg-white border border-slate-200 p-8" data-testid="landing-pricing-slider-card">
            <BandSlider bands={bands} count={count} onChange={setCount} testid="landing-band-slider" />
            <div className="mt-8">
              <BandReadout band={band} maxCap={maxCap} testid="landing-band-readout">
                <Link to="/signup" data-testid="landing-band-cta"
                  className="mt-6 inline-flex items-center gap-2 brand-accent-bg font-semibold px-6 py-3 text-[13px]">
                  {t("landing.requestTrial")} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                </Link>
              </BandReadout>
            </div>
            <ul className="text-[12px] text-slate-600 mt-6 space-y-2">
              {PLAN_BULLETS.map((b) => <li key={b}>{t(b)}</li>)}
            </ul>
          </div>
          <div className="lg:col-span-2 bg-white border border-slate-200" data-testid="landing-band-table">
            <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">{t("bands.allBands")}</p>
            </div>
            <table className="w-full text-[12px]">
              <tbody className="divide-y divide-slate-100">
                {bands.map((p) => {
                  const active = p.id === band?.id;
                  return (
                    <tr key={p.id} data-testid={`landing-band-row-${p.id}`}
                      className="cursor-pointer hover:bg-slate-50 transition-colors"
                      style={active ? {
                        boxShadow: "inset 3px 0 0 var(--brand-accent)",
                        background: "color-mix(in srgb, var(--brand-accent) 7%, white)",
                      } : {}}
                      onClick={() => setCount(p.contact ? maxCap + 25 : p.employee_cap)}>
                      <td className={`px-5 py-2 ${active ? "font-bold" : "font-medium"}`}
                        style={{ color: "var(--brand-primary)" }}>{p.name}</td>
                      <td className="px-3 py-2 font-mono text-slate-500 whitespace-nowrap">
                        {p.contact ? `${maxCap}+` : `≤ ${p.employee_cap}`}
                      </td>
                      <td className="px-5 py-2 font-mono text-right whitespace-nowrap"
                        style={{ color: active ? GOLD_INK : "#334155" }}>
                        {p.contact ? t("bands.contactUs") : `$${p.usd.toLocaleString()}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-6">
          {t("landing.pricingNoteUsd")}
        </p>
      </section>

      <footer className="border-t border-slate-200 py-8" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between flex-wrap gap-3">
          <p className="text-[11px] text-slate-400 font-mono">
            {t("landing.footerTagline")}
          </p>
          <div className="flex items-center gap-5">
            <a href={BROCHURE_URL} target="_blank" rel="noreferrer" data-testid="landing-footer-brochure"
              className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-slate-600">
              <FileDown className="h-3 w-3" /> {t("landing.downloadBrochure")}
            </a>
            <Link to="/login" className="text-[11px] text-slate-400 hover:text-slate-600">{t("landing.login")}</Link>
            <Link to="/signup" className="text-[11px] hover:underline" style={{ color: GOLD_INK }}>{t("landing.requestTrial")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
